import { X } from "lucide-react";
import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { useBodyScrollLock } from "@krea/shared/hooks/useBodyScrollLock";
import { cn } from "@krea/shared/utils";

import { Button } from "./Button";

/**
 * True for anything rendered inside a DrawerContent. A drawer opened from in
 * there must pass `nested` (below); ask this rather than guessing from the
 * viewport, since the same form can render both inside a drawer and on a page.
 */
const InsideDrawerContext = React.createContext(false);

const useIsInsideDrawer = () => React.useContext(InsideDrawerContext);

/**
 * The drag handle's look, in one place so every drawer shows the same grip.
 *
 * `!` on each property vaul also sets is required, not stylistic: vaul injects
 * an UNLAYERED `[data-vaul-handle]` rule at runtime (5px × 32px, a pale grey at
 * 70% opacity, 1rem radius), and unlayered CSS beats Tailwind's
 * `@layer utilities` whatever the specificity or source order. Without the
 * important modifier these classes are inert and the handle falls back to
 * vaul's thinner, washed-out default. `mt-3`/`mb-2` need no `!` — vaul sets no
 * vertical margin.
 *
 * `shrink-0` is load-bearing. The panel below is a flex column whose scroll
 * wrapper is `flex-1 min-h-0`, i.e. `flex: 1 1 0%` — a scaled shrink factor of
 * `1 × 0 = 0`. So when the content exceeds the panel's max-height, the flex
 * algorithm cannot reclaim space from the wrapper and takes it from the
 * fixed-size siblings instead: the handle rendered 3.8px instead of 6px in any
 * drawer tall enough to hit its cap.
 */
export const DRAWER_HANDLE_CLASS =
  "mx-auto mt-3 mb-2 h-1.5! w-10! shrink-0 rounded-full! bg-gray-300! opacity-100!";

/**
 * The nearest ancestor of `start`, up to and including `boundary`, that can
 * actually consume a vertical drag.
 *
 * Resolved per gesture rather than assumed to be the drawer's own scroll
 * wrapper, because a drawer may hand its scrolling to something inside it: the
 * mobile Select and the progress drawer both neutralise the wrapper with
 * `overflow-hidden` and scroll a list within it. Measuring the wrapper there
 * reports no room in either direction, which cancelled every gesture and left
 * the inner list unscrollable.
 *
 * `overflow-y` has to say auto/scroll — an `overflow-hidden` element whose
 * content is taller still cannot scroll, which is exactly the case above.
 */
const findScroller = (
  start: EventTarget | null,
  boundary: HTMLElement,
): HTMLElement | null => {
  let el = start instanceof Element ? start : null;
  while (el) {
    if (el instanceof HTMLElement && el.scrollHeight > el.clientHeight) {
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === "auto" || overflowY === "scroll") return el;
    }
    if (el === boundary) return null;
    el = el.parentElement;
  }
  return null;
};

/**
 * Whether a drag in this direction has nowhere left to go in `scroller`, i.e.
 * the point at which iOS would start panning the page instead.
 */
const isAtEnd = (scroller: HTMLElement, draggingDown: boolean): boolean =>
  draggingDown
    ? scroller.scrollTop <= 0
    : scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;

type IDrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  /**
   * Render vaul's NestedRoot instead of Root, for a drawer opened from inside
   * another drawer. A plain Root would leave the parent mounted underneath with
   * its own scroll lock and overlay, and neither panel would give way to the
   * other. Pair with a z-index above the parent's.
   */
  nested?: boolean;
};

const Drawer = ({
  shouldScaleBackground = false,
  handleOnly = true,
  // Off because vaul's built-in repositioning shrinks the drawer to near-zero
  // height. Nothing replaces it: the panel stays put and the keyboard overlays
  // its lower part, which the band below DrawerContent keeps opaque. Custom
  // handling was tried (lifting the panel, padding it, blur/refocus to redraw
  // the iOS caret) and left hit targets offset from what was painted once the
  // keyboard closed — worse than the keyboard covering a field.
  // vaul also lists this flag in its usePreventScroll disable expression, so
  // turning it off drops the page-scroll lock too — hence useBodyScrollLock.
  repositionInputs = false,
  nested = false,
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: IDrawerProps) => {
  const Root = nested ? DrawerPrimitive.NestedRoot : DrawerPrimitive.Root;

  // The lock follows the drawer being open, not its content being mounted:
  // mounted content outlives the open state, so a page rendering a drawer per
  // row would stay locked with every one of them closed.
  const [isOpen, setIsOpen] = React.useState(open ?? defaultOpen ?? false);

  React.useEffect(() => {
    if (open !== undefined) setIsOpen(open);
  }, [open]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setIsOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );

  useBodyScrollLock(isOpen);

  return (
    <Root
      shouldScaleBackground={shouldScaleBackground}
      handleOnly={handleOnly}
      repositionInputs={repositionInputs}
      // vaul's own lock is Safari-only; we own it instead so the two do not
      // both write document.body.
      noBodyStyles
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      {...props}
    />
  );
};
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-black/50 backdrop-blur-xs", className)}
    {...props}
  />
);
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

interface IDrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  hideCloseButton?: boolean;
  overlayClassName?: string;
  /** className for the children wrapper div. Defaults to "px-4 pb-8". Pass "" to opt out of default padding. */
  contentClassName?: string;
}

const DrawerContent = ({
  className,
  children,
  hideCloseButton = false,
  overlayClassName,
  contentClassName,
  style,
  ref,
  ...props
}: IDrawerContentProps & {
  ref?: React.Ref<React.ElementRef<typeof DrawerPrimitive.Content>>;
}) => {
  // State rather than a ref: this component mounts while the drawer is still
  // closed, so vaul has not rendered the panel yet and a mount effect would
  // find nothing. Opening does not re-render this component, so an effect
  // keyed on a ref alone never runs again — state gives the effect below
  // something that changes when the node actually appears.
  const [panelNode, setPanelNode] = React.useState<HTMLDivElement | null>(null);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      setPanelNode(node);
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  // iOS pans the page when a drag inside the drawer has nowhere else to go,
  // which the CSS lock does not stop — that prevents the document from
  // scrolling, not Safari from panning. vaul guards this in
  // preventScrollMobileSafari, but that is behind repositionInputs, which is
  // off because its repositioning collapses the drawer.
  //
  // Cancel only what would reach the page: a drag with no scrollable ancestor
  // inside the panel, or one pushing past an end of the one it has. While that
  // scroller has room the gesture is left alone, so it scrolls normally no
  // matter what it started on — fields and checkboxes included, which is most
  // of the surface of a form drawer.
  React.useEffect(() => {
    if (!panelNode) return;
    let lastY = 0;
    let scroller: HTMLElement | null = null;

    const onTouchStart = (e: TouchEvent) => {
      lastY = e.touches[0]?.clientY ?? 0;
      scroller = findScroller(e.target, panelNode);
    };

    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY ?? lastY;
      const draggingDown = y > lastY;
      lastY = y;

      if ((!scroller || isAtEnd(scroller, draggingDown)) && e.cancelable) {
        e.preventDefault();
      }
    };

    const onTouchEnd = () => {
      scroller = null;
    };

    panelNode.addEventListener("touchstart", onTouchStart, { passive: true });
    panelNode.addEventListener("touchmove", onTouchMove, { passive: false });
    panelNode.addEventListener("touchend", onTouchEnd, { passive: true });
    panelNode.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      panelNode.removeEventListener("touchstart", onTouchStart);
      panelNode.removeEventListener("touchmove", onTouchMove);
      panelNode.removeEventListener("touchend", onTouchEnd);
      panelNode.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [panelNode]);

  return (
    <DrawerPortal>
      <DrawerOverlay className={overlayClassName} />
      <DrawerPrimitive.Content
        ref={setRefs}
        style={style}
        className={cn(
          "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto max-h-[90svh] flex-col rounded-t-[10px] border bg-white",
          className,
        )}
        {...props}
      >
        <DrawerPrimitive.Handle className={DRAWER_HANDLE_CLASS} />
        {!hideCloseButton && (
          // shrink-0 for the same reason as the handle: this row would
          // otherwise absorb the overflow of a capped panel.
          <div className="flex shrink-0 justify-end px-2">
            <DrawerClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close"
                className="size-11 md:size-10"
              >
                <X className="size-6" strokeWidth={2} />
                <span className="sr-only">Close</span>
              </Button>
            </DrawerClose>
          </div>
        )}
        <div
          className={cn(
            "min-h-0 flex-1 overflow-y-auto overflow-x-hidden",
            "[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-3xl",
            contentClassName !== undefined ? contentClassName : "px-4 pb-8",
          )}
        >
          <InsideDrawerContext value={true}>{children}</InsideDrawerContext>
        </div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
};
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn("text-section leading-none tracking-tight", className)}
    {...props}
  />
);
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
);
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
  useIsInsideDrawer,
};
